import React from 'react';
import { format } from 'date-fns';
import {
  Plus,
  Pencil,
  Move,
  Trash2,
  UserPlus,
  UserMinus,
  Hand,
  CheckCircle,
  XCircle,
  Clock,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '@/lib/core/utils';
import type { ShiftSchedulingActivityEventWithDetails } from '@/types/shift-scheduling-activity';
import { SCHEDULING_EVENT_CONFIG } from '@/types/shift-scheduling-activity';
import { getSchedulingActivityDescription } from '@/hooks/scheduling/useShiftSchedulingActivityLog';

interface CompactShiftActivityLogRowProps {
  event: ShiftSchedulingActivityEventWithDetails;
  isSelected?: boolean;
  isNew?: boolean;
  onClick?: () => void;
}

const ICON_MAP: Record<string, LucideIcon> = {
  Plus,
  Pencil,
  Move,
  Trash2,
  UserPlus,
  UserMinus,
  Hand,
  CheckCircle,
  XCircle,
  Clock,
};

export const CompactShiftActivityLogRow: React.FC<CompactShiftActivityLogRowProps> = ({
  event,
  isSelected = false,
  isNew = false,
  onClick,
}) => {
  const config = SCHEDULING_EVENT_CONFIG[event.event_type];
  const Icon = ICON_MAP[config.icon] || Clock;
  const description = getSchedulingActivityDescription(event);

  const eventTime = new Date(event.event_timestamp);
  const timeDisplay = format(eventTime, 'h:mm a');

  return (
    <div
      className={cn(
        'flex items-center gap-2 py-1.5 px-2 text-sm cursor-pointer transition-colors',
        'hover:bg-muted/30',
        isSelected && 'border-l-2 border-primary bg-muted/50',
        !isSelected && 'border-l-2 border-transparent',
        isNew && 'bg-primary/5'
      )}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if ((e.key === 'Enter' || e.key === ' ') && onClick) {
          e.preventDefault();
          onClick();
        }
      }}
    >
      {/* Small icon without background */}
      <Icon className={cn('h-3.5 w-3.5 flex-shrink-0', config.color)} />

      {/* Description - truncated */}
      <span className="flex-1 truncate text-foreground/90">{description}</span>

      {/* Time on right */}
      <span className="flex-shrink-0 text-xs text-muted-foreground tabular-nums">
        {timeDisplay}
      </span>
    </div>
  );
};

export default CompactShiftActivityLogRow;
